import { useListState } from "@/hooks/use-list-state";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { PARTNER_APPLICATION_STATUSES, PARTNER_TYPES } from "@shared/schema";
import type { PartnerApplication } from "@shared/schema";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ClipboardList, Info, Loader2, Plus, X, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function PartnerApplicationsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useListState<string>("partner-applications:status", "pending");
  const [typeFilter, setTypeFilter] = useListState<string>("partner-applications:type", "all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [actionDialog, setActionDialog] = useState<{ open: boolean; action: "reject" | "needs_more_info"; app: PartnerApplication | null; notes: string }>({ open: false, action: "reject", app: null, notes: "" });
  const emptyInvite = { partnerType: PARTNER_TYPES[0] as string, firstName: "", lastName: "", companyName: "", email: "" };
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(emptyInvite);

  const appsQuery = useQuery<PartnerApplication[]>({
    queryKey: ["/api/admin/partners/applications", statusFilter, typeFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter && typeFilter !== "all") params.set("partnerType", typeFilter);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      const res = await fetch(`/api/admin/partners/applications?${params}`, { credentials: "include" });
      return res.json();
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/admin/partners/applications/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners/applications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      toast({ title: "Application approved", description: "Partner account created. We emailed a sign-in link and temporary password so they can e-sign the agreement." });
    },
    onError: (err: any) => toast({ title: "Failed to approve", description: parseErrorMessage(err, "Couldn't approve. Please try again."), variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) =>
      apiRequest("POST", `/api/admin/partners/applications/${id}/reject`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners/applications"] });
      setActionDialog(d => ({ ...d, open: false }));
      toast({ title: "Application rejected", description: "Rejection email sent." });
    },
    onError: (err: any) => toast({ title: "Failed to reject", description: parseErrorMessage(err, "Couldn't reject. Please try again."), variant: "destructive" }),
  });

  const needsMoreInfoMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) =>
      apiRequest("POST", `/api/admin/partners/applications/${id}/needs-more-info`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners/applications"] });
      setActionDialog(d => ({ ...d, open: false }));
      toast({ title: "Follow-up sent", description: "Applicant notified via email." });
    },
    onError: (err: any) => toast({ title: "Failed", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });

  const inviteMutation = useMutation({
    mutationFn: async (form: typeof emptyInvite) =>
      apiRequest("POST", `/api/admin/partners/invite`, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners/applications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      setInviteOpen(false);
      setInviteForm(emptyInvite);
      toast({ title: "Partner invited", description: "Account created and setup email sent." });
    },
    onError: (err: any) => toast({ title: "Failed to invite", description: parseErrorMessage(err, "Couldn't invite. Please try again."), variant: "destructive" }),
  });

  const typeLabel = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  const statusVariant = (s: string): "default" | "secondary" | "destructive" | "outline" => {
    if (s === "approved") return "default";
    if (s === "conditionally_approved") return "default";
    if (s === "rejected") return "destructive";
    if (s === "needs_more_info") return "outline";
    return "secondary";
  };

  const apps = appsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="select-app-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PARTNER_APPLICATION_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-app-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">From</span>
          <Input type="date" className="h-8 text-xs w-[130px]" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="input-app-date-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" className="h-8 text-xs w-[130px]" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="input-app-date-to" />
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="button-clear-dates">
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{apps.length} application{apps.length !== 1 ? "s" : ""}</span>
        <Button size="sm" className="h-8 text-xs" onClick={() => { setInviteForm(emptyInvite); setInviteOpen(true); }} data-testid="button-invite-partner">
          <Plus className="h-3.5 w-3.5 mr-1" />Invite Partner
        </Button>
      </div>

      <Card>
        {appsQuery.isLoading ? (
          <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : apps.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No applications found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map(app => (
                  <TableRow key={app.id} data-testid={`row-application-${app.id}`}>
                    <TableCell className="font-medium">{app.firstName} {app.lastName}</TableCell>
                    <TableCell>{app.companyName}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{typeLabel(app.partnerType)}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm">{app.email}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(app.status)} className="text-xs">
                        {app.status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {(app.status === "pending" || app.status === "needs_more_info") && (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" className="h-7 text-xs" variant="default"
                            onClick={() => approveMutation.mutate(app.id)}
                            disabled={approveMutation.isPending}
                            data-testid={`button-approve-app-${app.id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve
                          </Button>
                          <Button size="sm" className="h-7 text-xs" variant="outline"
                            onClick={() => setActionDialog({ open: true, action: "needs_more_info", app, notes: app.adminNotes || "" })}
                            data-testid={`button-moreinfo-app-${app.id}`}
                          >
                            <Info className="h-3.5 w-3.5 mr-1" />Info
                          </Button>
                          <Button size="sm" className="h-7 text-xs" variant="destructive"
                            onClick={() => setActionDialog({ open: true, action: "reject", app, notes: "" })}
                            data-testid={`button-reject-app-${app.id}`}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {actionDialog.app && (
        <Dialog open={actionDialog.open} onOpenChange={open => setActionDialog(d => ({ ...d, open }))}>
          <DialogContent data-testid="dialog-application-action">
            <DialogHeader>
              <DialogTitle>{actionDialog.action === "reject" ? "Reject Application" : "Request More Information"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {actionDialog.action === "reject"
                  ? `You are rejecting the application from ${actionDialog.app.firstName} ${actionDialog.app.lastName} (${actionDialog.app.companyName}). An email will be sent notifying them of this decision.`
                  : `Describe what additional information you need from ${actionDialog.app.firstName} ${actionDialog.app.lastName}.`
                }
              </p>
              {actionDialog.action === "needs_more_info" && (
                <div>
                  <Label className="text-xs">Notes for applicant <span className="text-destructive">*</span></Label>
                  <Textarea
                    value={actionDialog.notes}
                    onChange={e => setActionDialog(d => ({ ...d, notes: e.target.value }))}
                    placeholder="What do you need from them?"
                    rows={4}
                    className="mt-1"
                    data-testid="input-action-notes"
                  />
                </div>
              )}
              {actionDialog.action === "reject" && (
                <div>
                  <Label className="text-xs">Internal notes (optional, not sent to applicant)</Label>
                  <Textarea
                    value={actionDialog.notes}
                    onChange={e => setActionDialog(d => ({ ...d, notes: e.target.value }))}
                    placeholder="Why are you rejecting this application?"
                    rows={3}
                    className="mt-1"
                    data-testid="input-reject-notes"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialog(d => ({ ...d, open: false }))} data-testid="button-cancel-action">Cancel</Button>
              <Button
                variant={actionDialog.action === "reject" ? "destructive" : "default"}
                disabled={actionDialog.action === "needs_more_info" && !actionDialog.notes.trim()}
                onClick={() => {
                  if (!actionDialog.app) return;
                  if (actionDialog.action === "reject") {
                    rejectMutation.mutate({ id: actionDialog.app.id, notes: actionDialog.notes });
                  } else {
                    needsMoreInfoMutation.mutate({ id: actionDialog.app.id, notes: actionDialog.notes });
                  }
                }}
                data-testid="button-confirm-action"
              >
                {(rejectMutation.isPending || needsMoreInfoMutation.isPending) && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {actionDialog.action === "reject" ? "Reject & Notify" : "Send Follow-up"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={inviteOpen} onOpenChange={open => { if (inviteMutation.isPending) return; setInviteOpen(open); }}>
        <DialogContent
          data-testid="dialog-invite-partner"
          onInteractOutside={e => { if (inviteMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={e => { if (inviteMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={e => { if (inviteMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Invite Partner</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Create a partner account directly and send them a setup link — no public application needed.
          </p>
          <div className="space-y-3 mt-1">
            <div>
              <Label className="text-xs">Partner type</Label>
              <Select value={inviteForm.partnerType} onValueChange={v => setInviteForm(f => ({ ...f, partnerType: v }))}>
                <SelectTrigger className="mt-1" data-testid="select-invite-partner-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First name <span className="text-destructive">*</span></Label>
                <Input value={inviteForm.firstName} onChange={e => setInviteForm(f => ({ ...f, firstName: e.target.value }))} className="mt-1" data-testid="input-invite-first-name" />
              </div>
              <div>
                <Label className="text-xs">Last name <span className="text-destructive">*</span></Label>
                <Input value={inviteForm.lastName} onChange={e => setInviteForm(f => ({ ...f, lastName: e.target.value }))} className="mt-1" data-testid="input-invite-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Company <span className="text-destructive">*</span></Label>
              <Input value={inviteForm.companyName} onChange={e => setInviteForm(f => ({ ...f, companyName: e.target.value }))} className="mt-1" data-testid="input-invite-company" />
            </div>
            <div>
              <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
              <Input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="input-invite-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteMutation.isPending} data-testid="button-cancel-invite">Cancel</Button>
            <Button
              onClick={() => inviteMutation.mutate(inviteForm)}
              disabled={inviteMutation.isPending || !inviteForm.firstName.trim() || !inviteForm.lastName.trim() || !inviteForm.companyName.trim() || !inviteForm.email.trim()}
              data-testid="button-confirm-invite"
            >
              {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
